/**
 * ENG-8575: the per-agent path-hash salt for the tool-call audit log.
 *
 * ENG-8573 hashes file paths under HMAC-SHA256 so the control plane can see
 * that an agent touched the same file twice without learning the file's name.
 * That protection is entirely carried by this salt: a bare SHA-256 of a file
 * path is reversible with a wordlist, because the space of plausible paths is
 * small and guessable. So the redaction module emits NOTHING for a file target
 * when the salt is absent, rather than degrading to an unsalted digest.
 *
 * WHY THIS MODULE EXISTS AT ALL. `ScanArgs.pathHashSalt` was a required input
 * with no producer anywhere outside the tests — slices 1 and 2 defined the
 * contract and left the key to whoever armed the scanner. Wiring the scanner
 * without this would have been silently destructive rather than loudly broken:
 * every Read/Write/Edit target would resolve to kind `'none'`, the table would
 * fill with Bash and WebFetch rows, and the audit surface would look healthy
 * while being blind to the single largest class of tool call. Nothing logs an
 * error on that path, because emitting nothing IS the designed behaviour when
 * the salt is missing — it just was not supposed to be missing forever.
 *
 * THE SALT NEVER LEAVES THE HOST. It is not in any request body, any log line,
 * or any coverage row. `readToolCallPathSalt` is the only reader, and the value
 * flows straight into `createHmac` inside the redaction module.
 *
 * STABILITY IS THE WHOLE POINT. Correlation ("this agent opened the same file
 * twelve times") only works while the salt is unchanged, so this is generated
 * once per agent and persisted. Rotating it is not corruption — old rows stay
 * valid and old hashes stay internally consistent — but hashes either side of a
 * rotation no longer join, so a rotation is a deliberate act, not something a
 * restart should cause. Hence: generate only when the file is genuinely absent.
 */

import { randomBytes } from 'node:crypto';
import { existsSync, mkdirSync, readFileSync, renameSync, unlinkSync, writeFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { dirname, join } from 'node:path';

import { agentRuntimeKey } from './agent-runtime-key.js';

/** 32 bytes, hex-encoded. Comfortably beyond HMAC-SHA256's useful key length. */
const SALT_BYTES = 32;

/** A well-formed salt: exactly the shape `generate` produces. */
const SALT_RE = /^[0-9a-f]{64}$/;

/**
 * Where the salt lives. Deliberately a sibling of `tool-call-cursors.json` and
 * resolved the same way — through `agentRuntimeKey`, so the salt follows an
 * agent across the ADR-0049 id-keyed migration instead of being stranded under
 * an old codename. A stranded salt would silently regenerate and break hash
 * correlation across the migration boundary, which is exactly the invisible
 * failure this file is trying not to have.
 */
export function pathSaltPath(codeName: string, homeDir?: string): string {
  const home = homeDir ?? (process.env['HOME']?.trim() || homedir());
  const key = agentRuntimeKey(codeName, homeDir);
  return join(home, '.augmented', key, 'tool-call-path-salt');
}

/**
 * Read the agent's salt, generating and persisting one on first use.
 *
 * Returns `null` if the salt can neither be read nor written. That is the
 * correct failure direction and not a fallback: the caller passes the empty
 * result through to the redaction context, which withholds file targets. A
 * generated-but-unpersisted salt would be worse than none, because every tick
 * would produce different hashes for the same path — filling the table with
 * uncorrelatable noise that still looks like data.
 */
export function readToolCallPathSalt(codeName: string, homeDir?: string): string | null {
  let file: string;
  try {
    file = pathSaltPath(codeName, homeDir);
  } catch {
    return null;
  }

  try {
    if (existsSync(file)) {
      const existing = readFileSync(file, 'utf-8').trim();
      // A truncated or hand-edited file is replaced rather than used: a short
      // salt is a weak salt, and silently accepting one would quietly reduce
      // the protection to whatever entropy survived.
      if (SALT_RE.test(existing)) return existing;
    }
  } catch {
    // Fall through to generation — an unreadable file is treated as absent.
  }

  const salt = randomBytes(SALT_BYTES).toString('hex');
  const tmp = `${file}.tmp.${process.pid}`;
  try {
    mkdirSync(dirname(file), { recursive: true, mode: 0o700 });
    // 0600: the salt is the only thing standing between the control plane and
    // a reversible path hash, so it is no more readable than a private key.
    //
    // Written to a temp file and renamed, so a crash mid-write cannot leave a
    // truncated salt behind. A truncated salt would fail SALT_RE on the next
    // read and regenerate — which is safe, but it is a SILENT rotation, and a
    // rotation nobody chose is precisely what breaks hash correlation without
    // anything reporting that it broke.
    writeFileSync(tmp, `${salt}\n`, { encoding: 'utf-8', mode: 0o600 });
    renameSync(tmp, file);
  } catch {
    try {
      unlinkSync(tmp);
    } catch {
      // Best effort; a stray temp file is harmless — it is never read.
    }
    return null;
  }
  return salt;
}
